export interface ProductParameter {
  key: string;
  value: string;
  unit: string;
}

export interface ProductTemporaryFile {
  name: string;
  type: string;
  size: number;
}

export interface ProductDraftFields {
  manufacturer: string;
  name: string;
  model: string;
  category: string;
  purpose: string;
  features: string;
  claims: string;
  limits: string;
  scenes: string;
  source: string;
  reason: string;
  parameters: ProductParameter[];
  files: ProductTemporaryFile[];
}

export interface ProductDraftErrors {
  [field: string]: string;
}

export const productDemoCategories = ['侦察与监测设备', '灭火装备', '无人机', '个人防护', '其他'];

export function emptyProductDraft(): ProductDraftFields {
  return {
    manufacturer: 'XX科技 · 模拟显示标识',
    name: '',
    model: '',
    category: '',
    purpose: '',
    features: '',
    claims: '',
    limits: '',
    scenes: '',
    source: '',
    reason: '',
    parameters: [],
    files: [],
  };
}

export function cloneProductFields(fields: ProductDraftFields): ProductDraftFields {
  return {
    ...fields,
    parameters: fields.parameters.map((parameter) => ({ ...parameter })),
    files: fields.files.map((file) => ({ ...file })),
  };
}

const unsafeText = /[<>]|javascript\s*:|data\s*:/i;

export function unsafeProductText(value: string): boolean {
  return (
    unsafeText.test(value) ||
    Array.from(value).some(
      (character) => character.charCodeAt(0) < 32 && ![9, 10, 13].includes(character.charCodeAt(0)),
    )
  );
}

export function validateProductTemporaryFile(file: ProductTemporaryFile): string | null {
  if (!file.name.trim() || file.name.length > 160 || unsafeProductText(file.name))
    return '文件名无效、过长或包含危险字符';
  if (!Number.isFinite(file.size) || file.size <= 0 || file.size > 5 * 1024 * 1024)
    return '每个文件须非空且不超过 5 MB';
  const match = /\.(png|jpg|jpeg|pdf)$/i.exec(file.name);
  if (!match) return '只接受 PNG、JPEG、PDF 的临时文件名';
  const expected =
    match[1]?.toLowerCase() === 'pdf'
      ? 'application/pdf'
      : match[1]?.toLowerCase() === 'png'
        ? 'image/png'
        : 'image/jpeg';
  if (file.type !== expected) return '文件扩展名与浏览器 MIME 不匹配（内容和权利未核验）';
  return null;
}

export function productFieldDifferences(
  baseline: ProductDraftFields | null,
  fields: ProductDraftFields,
): { label: string; before: string; after: string }[] {
  if (!baseline)
    return [{ label: '版本来源', before: '未知 · 无历史版本', after: '本次会话新草稿' }];
  const differences = Object.keys(fieldLimits)
    .map((key) => {
      const field = key as keyof typeof fieldLimits;
      return {
        label: fieldLabels[field],
        before: baseline[field] || '未提供',
        after: fields[field] || '未提供',
      };
    })
    .filter((change) => change.before !== change.after);
  if (JSON.stringify(baseline.parameters) !== JSON.stringify(fields.parameters))
    differences.push({
      label: '技术参数',
      before:
        baseline.parameters.map((p) => `${p.key}: ${p.value} ${p.unit}`).join('；') || '未提供',
      after: fields.parameters.map((p) => `${p.key}: ${p.value} ${p.unit}`).join('；') || '未提供',
    });
  if (JSON.stringify(baseline.files) !== JSON.stringify(fields.files))
    differences.push({
      label: '临时附件名',
      before: baseline.files.map((f) => f.name).join('；') || '待上传',
      after: fields.files.map((f) => f.name).join('；') || '待上传',
    });
  return differences;
}

export const fieldLimits: Record<
  Exclude<keyof ProductDraftFields, 'parameters' | 'files'>,
  number
> = {
  manufacturer: 80,
  name: 80,
  model: 60,
  category: 40,
  purpose: 600,
  features: 1000,
  claims: 1000,
  limits: 1000,
  scenes: 600,
  source: 600,
  reason: 600,
};

export const fieldLabels: Record<keyof typeof fieldLimits, string> = {
  manufacturer: '厂商显示标识',
  name: '产品名称',
  model: '型号',
  category: '产品类别',
  purpose: '用途简介',
  features: '核心特点',
  claims: '厂家能力声明',
  limits: '安全与技术限制',
  scenes: '厂家场景声明',
  source: '资料来源',
  reason: '修订说明',
};
